import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Trophy, Users, ThumbsUp, Trash2, Search, 
  Video, Eye, EyeOff, RefreshCw, Crown, Medal,
  CheckCircle, XCircle, Download, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContestManagement = () => {
  const [entries, setEntries] = useState([]);
  const [voters, setVoters] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeView, setActiveView] = useState('entries'); // entries, voters, videos

  useEffect(() => {
    fetchContestData();
  }, []);

  const fetchContestData = async () => {
    setLoading(true);
    try {
      const [entriesRes, statsRes, votersRes] = await Promise.all([
        axios.get(`${API}/admin/contest/entries`, { withCredentials: true }),
        axios.get(`${API}/contest/stats`),
        axios.get(`${API}/admin/contest/voters`, { withCredentials: true })
      ]);
      
      setEntries(entriesRes.data.entries || []);
      setStats(statsRes.data);
      setVoters(votersRes.data.voters || []);
    } catch (error) {
      console.error('Error fetching contest data:', error);
      toast.error('Failed to load contest data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (entryId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'disqualified' : 'active';
      await axios.put(
        `${API}/admin/contest/entries/${entryId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Entry ${newStatus === 'active' ? 'activated' : 'disqualified'}`);
      fetchContestData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this contest entry? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/contest/entries/${entryId}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchContestData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEntries.length === 0) {
      toast.error('No entries selected');
      return;
    }
    
    if (!confirm(`Delete ${selectedEntries.length} contest entries? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/contest/entries/bulk-delete`,
        { entry_ids: selectedEntries },
        { withCredentials: true }
      );
      toast.success(`Deleted ${selectedEntries.length} entries`);
      setSelectedEntries([]);
      fetchContestData();
    } catch (error) {
      toast.error('Failed to delete entries');
    }
  };

  const handleResetVotes = async (entryId) => {
    if (!confirm('Reset all votes for this entry? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/contest/entries/${entryId}/reset-votes`,
        {},
        { withCredentials: true }
      );
      toast.success('Votes reset');
      fetchContestData();
    } catch (error) {
      toast.error('Failed to reset votes');
    }
  };

  const handleAddBonusVotes = async (entryId, bonusVotes) => {
    try {
      await axios.post(
        `${API}/admin/contest/entries/${entryId}/add-votes`,
        { bonus_votes: bonusVotes },
        { withCredentials: true }
      );
      toast.success(`Added ${bonusVotes} bonus votes`);
      fetchContestData();
    } catch (error) {
      toast.error('Failed to add bonus votes');
    }
  };

  const exportContestData = () => {
    const csvData = entries.map(entry => ({
      rank: entries.indexOf(entry) + 1,
      business_name: entry.business_name,
      name: entry.name,
      email: entry.email,
      phone: entry.phone,
      business_type: entry.business_type,
      votes: entry.votes,
      status: entry.status,
      created_at: entry.created_at
    }));
    
    const headers = Object.keys(csvData[0] || {}).join(',');
    const rows = csvData.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contest-entries-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredEntries = entries.filter(entry =>
    entry.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredVoters = voters.filter(voter =>
    voter.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    voter.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-medium text-gray-500">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading contest data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Total Contestants</p>
              <p className="text-3xl font-bold">{stats?.total_entries || 0}</p>
            </div>
            <Trophy className="w-10 h-10 opacity-80" />
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Total Votes</p>
              <p className="text-3xl font-bold">{stats?.total_votes || 0}</p>
            </div>
            <ThumbsUp className="w-10 h-10 opacity-80" />
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Registered Voters</p>
              <p className="text-3xl font-bold">{voters.length}</p>
            </div>
            <Users className="w-10 h-10 opacity-80" />
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Current Leader</p>
              <p className="text-lg font-bold truncate">{stats?.leader?.business_name || 'N/A'}</p>
              <p className="text-sm opacity-80">{stats?.leader?.votes || 0} votes</p>
            </div>
            <Crown className="w-10 h-10 opacity-80" />
          </div>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setActiveView('entries')}
          variant={activeView === 'entries' ? 'default' : 'outline'}
          className={activeView === 'entries' ? 'bg-orange-500 hover:bg-orange-600' : ''}
        >
          <Trophy className="w-4 h-4 mr-2" />
          Contestants ({entries.length})
        </Button>
        <Button
          onClick={() => setActiveView('voters')}
          variant={activeView === 'voters' ? 'default' : 'outline'}
          className={activeView === 'voters' ? 'bg-orange-500 hover:bg-orange-600' : ''}
        >
          <Users className="w-4 h-4 mr-2" />
          Voters ({voters.length})
        </Button>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder={`Search ${activeView}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Button onClick={fetchContestData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
        
        {activeView === 'entries' && (
          <>
            <Button onClick={exportContestData} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            
            {selectedEntries.length > 0 && (
              <Button onClick={handleBulkDelete} variant="destructive" size="sm">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected ({selectedEntries.length})
              </Button>
            )}
          </>
        )}
      </div>

      {/* Contestants List */}
      {activeView === 'entries' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedEntries.length === filteredEntries.length && filteredEntries.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEntries(filteredEntries.map(e => e.id));
                        } else {
                          setSelectedEntries([]);
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Votes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No contest entries found
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry, index) => (
                    <tr key={entry.id} className={`hover:bg-gray-50 ${entry.status !== 'active' ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedEntries.includes(entry.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEntries([...selectedEntries, entry.id]);
                            } else {
                              setSelectedEntries(selectedEntries.filter(id => id !== entry.id));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center w-8 h-8">
                          {getRankBadge(index + 1)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{entry.business_name}</p>
                          <p className="text-sm text-gray-500">{entry.business_type}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-gray-900">{entry.name}</p>
                          <p className="text-xs text-gray-500">{entry.email}</p>
                          <p className="text-xs text-gray-500">{entry.phone}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-orange-600">{entry.votes}</span>
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleAddBonusVotes(entry.id, 5)}
                              className="text-xs text-blue-600 hover:underline"
                              title="Add 5 bonus votes (for video submission)"
                            >
                              +5
                            </button>
                            <button
                              onClick={() => handleResetVotes(entry.id)}
                              className="text-xs text-red-600 hover:underline"
                              title="Reset votes"
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          entry.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {entry.status === 'active' ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Active</>
                          ) : (
                            <><XCircle className="w-3 h-3 mr-1" /> Disqualified</>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleToggleStatus(entry.id, entry.status)}
                            variant="outline"
                            size="sm"
                            className={entry.status === 'active' ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}
                          >
                            {entry.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            onClick={() => handleDeleteEntry(entry.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Voters List */}
      {activeView === 'voters' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Votes Cast</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredVoters.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      No registered voters found
                    </td>
                  </tr>
                ) : (
                  filteredVoters.map((voter) => (
                    <tr key={voter.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{voter.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{voter.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{voter.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(voter.registered_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <ThumbsUp className="w-3 h-3 mr-1" />
                          {voter.total_votes || 0}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ContestManagement;
